import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Empty,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  InboxOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import moment from 'moment-jalaali';
import MainLayout from '../../layouts/MainLayout';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import WorkflowActionModal from '../../components/workflow/WorkflowActionModal';
import WorkflowTimeline from '../../components/workflow/WorkflowTimeline';
import { workflowApi } from '../../utils/api';
import { toPersianDigits } from '../../utils/formatPriceToman';

const MODULE_OPTIONS = [
  { value: '', label: 'همه موارد' },
  { value: 'leave', label: 'مرخصی' },
  { value: 'overtime', label: 'اضافه‌کاری' },
  { value: 'regularization', label: 'اصلاح تردد' },
  { value: 'mission', label: 'مأموریت' },
  { value: 'expense', label: 'مالی' },
  { value: 'remote', label: 'دورکاری' },
];

const MODULE_LABELS = Object.fromEntries(
  MODULE_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
);

const resolveApprovableLink = (item) => {
  const type = item?.instance?.workflow_definition?.module_type
    || item?.instance?.module_type
    || '';
  const id = item?.instance?.approvable_id || item?.instance?.approvable?.id;
  if (!id) return null;
  if (type === 'leave' || String(item?.instance?.approvable_type || '').includes('Leave')) {
    return `/hr/leaves/${id}`;
  }
  if (type === 'overtime') return '/hr/overtime-requests';
  if (type === 'regularization') return '/hr/attendance-regularizations';
  if (type === 'mission') return '/hr/attendance-missions';
  return null;
};

const summarizeApprovable = (item) => {
  const moduleType =
    item?.instance?.workflow_definition?.module_type || item?.instance?.module_type || '';
  const ap = item?.instance?.approvable || {};
  const requester = item?.instance?.requester?.name || 'کارمند';
  const moduleLabel = MODULE_LABELS[moduleType] || 'درخواست';
  if (moduleType === 'leave' || ap.leave_date || ap.duration) {
    const date = ap.leave_date
      ? moment(ap.leave_date).format('jYYYY/jMM/jDD')
      : ap.multi_start_date
        ? `${moment(ap.multi_start_date).format('jYYYY/jMM/jDD')}`
        : '';
    return `${moduleLabel} — ${requester}${date ? ` (${date})` : ''}`;
  }
  if (moduleType === 'mission' || ap.destination || ap.trip_type) {
    const dest = ap.destination ? ` — ${ap.destination}` : '';
    const start = ap.start_date
      ? moment(ap.start_date).format('jYYYY/jMM/jDD')
      : ap.jalali_start_date || '';
    return `${moduleLabel} — ${requester}${dest}${start ? ` (${start})` : ''}`;
  }
  return `${moduleLabel} — ${requester}`;
};

const WorkflowInbox = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, per_page: 15, total: 0 });
  const [moduleType, setModuleType] = useState('');
  const [page, setPage] = useState(1);
  const [actionOpen, setActionOpen] = useState(false);
  const [actionMode, setActionMode] = useState('approve');
  const [actionTarget, setActionTarget] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailInstance, setDetailInstance] = useState(null);

  const load = useCallback(async (pageNum = page) => {
    setLoading(true);
    try {
      const res = await workflowApi.getPendingApprovals({
        module_type: moduleType || undefined,
        page: pageNum,
        per_page: meta.per_page || 15,
      });
      const payload = res?.data ?? res ?? {};
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.data)
          ? payload.data
          : Array.isArray(payload.items)
            ? payload.items
            : [];
      const pagination = res?.meta || payload.meta || {};
      setRows(list);
      setMeta({
        current_page: pagination.current_page || pageNum,
        per_page: pagination.per_page || 15,
        total: pagination.total ?? list.length,
        last_page: pagination.last_page,
      });
    } catch (error) {
      console.error(error);
      message.error(error?.message || 'خطا در دریافت کارتابل تأییدات');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [moduleType, page, meta.per_page]);

  useEffect(() => {
    load(page);
  }, [page, moduleType]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAction = (row, mode) => {
    setActionTarget(row);
    setActionMode(mode);
    setActionOpen(true);
  };

  const openTimeline = async (row) => {
    const instanceId = row.workflow_instance_id || row.instance?.id;
    if (instanceId == null) {
      message.warning('شناسه فرآیند موجود نیست');
      return;
    }
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailInstance(null);
    try {
      const res = await workflowApi.getInstance(instanceId);
      const payload = res?.data ?? res ?? {};
      const nested =
        payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
          ? payload.data
          : payload;
      setDetailInstance(nested);
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت مراحل تأیید');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>متقاضی</span>,
        key: 'requester',
        width: 200,
        render: (_, row) => {
          const u = row.instance?.requester || {};
          return (
            <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
              <Avatar size={36} src={u.image} icon={<UserOutlined />} />
              <div>
                <div className="font-medium">{u.name || '—'}</div>
                <div className="text-xs text-slate-500">
                  {u.employee_detail?.department?.team_name ||
                    u.employee_detail?.designation?.name ||
                    u.email ||
                    '—'}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>نوع / مورد</span>,
        key: 'module',
        render: (_, row) => {
          const moduleType =
            row.instance?.workflow_definition?.module_type || row.instance?.module_type;
          const link = resolveApprovableLink(row);
          return (
            <div style={{ fontFamily: 'CustomFont' }} className="space-y-1">
              <Tag color="blue" className="custom-font m-0">
                {MODULE_LABELS[moduleType] || moduleType || '—'}
              </Tag>
              <div className="text-sm text-slate-700 dark:text-slate-200">
                {summarizeApprovable(row)}
              </div>
              {row.instance?.workflow_definition?.name ? (
                <div className="text-[11px] text-slate-400">
                  {row.instance.workflow_definition.name}
                </div>
              ) : null}
              {link ? (
                <Link to={link} className="text-xs text-teal-700">
                  مشاهده جزئیات
                </Link>
              ) : null}
            </div>
          );
        },
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>مرحله جاری</span>,
        key: 'step',
        width: 180,
        render: (_, row) => (
          <div style={{ fontFamily: 'CustomFont' }}>
            <div className="font-medium text-sm">{row.name || '—'}</div>
            <Tag color="warning" className="custom-font m-0 mt-1 text-[10px]">
              {row.status_label || 'در انتظار بررسی'}
            </Tag>
            <div className="text-[11px] text-slate-400 mt-1">
              {row.step_order != null && row.instance?.total_steps != null
                ? `مرحله ${toPersianDigits(row.step_order)} از ${toPersianDigits(row.instance.total_steps)}`
                : null}
            </div>
          </div>
        ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>پیشرفت</span>,
        key: 'progress',
        width: 100,
        render: (_, row) => {
          const pct = Number(row.instance?.progress_percentage);
          return (
            <span className="tabular-nums" style={{ fontFamily: 'CustomFont' }}>
              {Number.isFinite(pct) ? `${toPersianDigits(Math.round(pct))}٪` : '—'}
            </span>
          );
        },
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>عملیات</span>,
        key: 'actions',
        width: 260,
        render: (_, row) => (
          <Space wrap size={[4, 4]}>
            <Button
              size="small"
              type="primary"
              icon={<CheckOutlined />}
              className="bg-emerald-600 hover:bg-emerald-700 border-emerald-600"
              style={{ fontFamily: 'CustomFont' }}
              onClick={() => openAction(row, 'approve')}
            >
              تأیید
            </Button>
            <Button
              size="small"
              danger
              icon={<CloseOutlined />}
              style={{ fontFamily: 'CustomFont' }}
              onClick={() => openAction(row, 'reject')}
            >
              رد
            </Button>
            <Tooltip title="مراحل تأیید">
              <Button
                size="small"
                icon={<EyeOutlined />}
                style={{ fontFamily: 'CustomFont' }}
                onClick={() => openTimeline(row)}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [],
  );

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }} className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-rose-50 dark:bg-rose-900/20 flex items-center justify-center">
              <Badge count={meta.total || 0} overflowCount={99} size="small">
                <InboxOutlined className="text-rose-600 text-xl" />
              </Badge>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100 m-0">
                کارتابل تأییدات
              </h1>
            </div>
          </div>
          <Space>
            <Select
              value={moduleType}
              onChange={(v) => {
                setModuleType(v);
                setPage(1);
              }}
              options={MODULE_OPTIONS}
              style={{ width: 160, fontFamily: 'CustomFont' }}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => load(page)}
              loading={loading}
              style={{ fontFamily: 'CustomFont' }}
            >
              بروزرسانی
            </Button>
          </Space>
        </div>

        <Card size="small" className="border-gray-100 dark:border-gray-700">
          {loading && rows.length === 0 ? (
            <TableSkeleton rows={6} columns={5} />
          ) : (
            <Table
              rowKey={(r) => r.id || `${r.workflow_instance_id}-${r.step_order}`}
              columns={columns}
              dataSource={rows}
              loading={loading}
              scroll={{ x: 960 }}
              locale={{
                emptyText: (
                  <Empty
                    description={
                      <span style={{ fontFamily: 'CustomFont' }}>
                        موردی در انتظار تأیید شما نیست
                      </span>
                    }
                  />
                ),
              }}
              pagination={{
                current: meta.current_page,
                pageSize: meta.per_page,
                total: meta.total,
                onChange: (p) => setPage(p),
                showSizeChanger: false,
              }}
            />
          )}
        </Card>

        <WorkflowActionModal
          open={actionOpen}
          mode={actionMode}
          instanceId={actionTarget?.workflow_instance_id || actionTarget?.instance?.id}
          summaryTitle={actionTarget ? summarizeApprovable(actionTarget) : ''}
          requester={actionTarget?.instance?.requester}
          moduleType={
            actionTarget?.instance?.workflow_definition?.module_type ||
            actionTarget?.instance?.module_type
          }
          stepName={actionTarget?.name}
          onClose={() => {
            setActionOpen(false);
            setActionTarget(null);
          }}
          onSuccess={() => load(page)}
        />

        {/* جزئیات مراحل تأیید */}
        {detailOpen ? (
          <Card
            size="small"
            className="border-amber-100 dark:border-amber-900/40"
            title={
              <span style={{ fontFamily: 'CustomFont' }}>مراحل تأیید</span>
            }
            extra={
              <Button
                type="link"
                style={{ fontFamily: 'CustomFont' }}
                onClick={() => {
                  setDetailOpen(false);
                  setDetailInstance(null);
                }}
              >
                بستن
              </Button>
            }
          >
            {detailLoading ? (
              <TableSkeleton rows={3} columns={1} />
            ) : (
              <WorkflowTimeline workflow={detailInstance} />
            )}
          </Card>
        ) : null}
      </div>
    </MainLayout>
  );
};

export default WorkflowInbox;
